import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Paper,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import WebTerminal from '../components/WebTerminal';
import apiService from '../services/api';

const TerminalPage = () => {
  const { vmName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [vms, setVms] = useState([]);
  const [selectedVM, setSelectedVM] = useState(vmName || null);
  const [showVMSelector, setShowVMSelector] = useState(!vmName);
  const [fullscreen, setFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // VM 목록 조회
  const fetchVMs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVMs();
      setVms(response.data.vms || []);
    } catch (error) {
      enqueueSnackbar('VM 목록 조회 실패', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // VM 선택 처리
  const handleVMSelect = (vm) => {
    setSelectedVM(vm.name);
    setShowVMSelector(false);
    // URL 업데이트
    navigate(`/terminal/${vm.name}`, { replace: true });
  };

  // 터미널 닫기
  const handleTerminalClose = () => {
    setSelectedVM(null);
    setShowVMSelector(true);
    navigate('/terminal', { replace: true });
  };

  // 전체화면 토글
  const handleFullscreenToggle = () => {
    setFullscreen(!fullscreen);
  };

  // 검색된 VM 목록
  const filteredVMs = vms.filter(vm => 
    vm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (vm.state && vm.state.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // 실행 중인 VM만 필터링
  const runningVMs = filteredVMs.filter(vm => vm.state === 'running');

  useEffect(() => {
    // VM이 선택되지 않았을 때만 VM 목록 조회
    if (!selectedVM && showVMSelector) {
      fetchVMs();
    }
    
    // URL 파라미터에서 VM 이름이 있으면 자동 선택
    if (vmName && !selectedVM) {
      setSelectedVM(vmName);
      setShowVMSelector(false);
    }
  }, [vmName, selectedVM, showVMSelector]);

  // 전체화면 모드
  if (fullscreen && selectedVM) {
    return (
      <Box sx={{ height: '100vh', overflow: 'hidden' }}>
        <WebTerminal
          vmName={selectedVM}
          onClose={handleTerminalClose}
          fullscreen={true}
          onFullscreenToggle={handleFullscreenToggle}
        />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* 브레드크럼 */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          component="button"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          홈
        </Link>
        <Typography color="text.primary">터미널</Typography>
        {selectedVM && (
          <Typography color="text.primary">{selectedVM}</Typography>
        )}
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        웹 터미널
      </Typography>

      {selectedVM ? (
        // 터미널 화면
        <Box>
          <Typography variant="h6" gutterBottom>
            VM: {selectedVM}
          </Typography>
          <WebTerminal
            vmName={selectedVM}
            onClose={handleTerminalClose}
            fullscreen={false}
            onFullscreenToggle={handleFullscreenToggle}
          />
        </Box>
      ) : (
        // VM 선택 화면
        <Box>
          <Typography variant="h6" gutterBottom>
            접속할 VM을 선택하세요
          </Typography>
          
          {/* 안내 메시지 */}
          <Alert severity="info" sx={{ mb: 3 }}>
            터미널은 실행 중인 VM에만 접속할 수 있습니다. VM이 종료되어 있다면 먼저 시작해주세요.
          </Alert>

          {/* 검색 */}
          <TextField
            fullWidth
            placeholder="VM 이름 또는 상태로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* VM 목록 */}
          <Paper elevation={2}>
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>VM 목록을 불러오는 중...</Typography>
              </Box>
            ) : runningVMs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {filteredVMs.length === 0 
                    ? (searchTerm ? '검색 결과가 없습니다.' : 'VM이 없습니다.')
                    : '실행 중인 VM이 없습니다. VM을 먼저 시작해주세요.'
                  }
                </Typography>
              </Box>
            ) : (
              <List>
                {runningVMs.map((vm) => (
                  <ListItem key={vm.name} disablePadding>
                    <ListItemButton onClick={() => handleVMSelect(vm)}>
                      <ListItemIcon>
                        <ComputerIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={vm.name}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              메모리: {vm.memory}MB | CPU: {vm.vcpus}개
                            </Typography>
                            <Chip
                              label={vm.state === 'running' ? '실행 중' : vm.state}
                              color={vm.state === 'running' ? 'success' : 'default'}
                              size="small"
                              icon={vm.state === 'running' ? <PlayArrowIcon /> : <StopIcon />}
                            />
                            {vm.networks && vm.networks.length > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                IP: {vm.networks.map(n => n.ip_address).filter(Boolean).join(', ') || 'N/A'}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {/* 종료된 VM 목록도 표시 (참고용) */}
          {filteredVMs.length > runningVMs.length && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom color="text.secondary">
                종료된 VM (터미널 접속 불가)
              </Typography>
              <Paper elevation={1} sx={{ bgcolor: 'grey.50' }}>
                <List>
                  {filteredVMs
                    .filter(vm => vm.state !== 'running')
                    .map((vm) => (
                      <ListItem key={vm.name}>
                        <ListItemIcon>
                          <ComputerIcon color="disabled" />
                        </ListItemIcon>
                        <ListItemText
                          primary={vm.name}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                메모리: {vm.memory}MB | CPU: {vm.vcpus}개
                              </Typography>
                              <Chip
                                label={vm.state || '알 수 없음'}
                                color="default"
                                size="small"
                                icon={<StopIcon />}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                </List>
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* VM 선택 다이얼로그 (모바일용 예비) */}
      <Dialog 
        open={showVMSelector && selectedVM} 
        onClose={() => setShowVMSelector(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>VM 변경</DialogTitle>
        <DialogContent>
          <List>
            {runningVMs.map((vm) => (
              <ListItem key={vm.name} disablePadding>
                <ListItemButton onClick={() => handleVMSelect(vm)}>
                  <ListItemIcon>
                    <ComputerIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={vm.name}
                    secondary={`${vm.memory}MB RAM, ${vm.vcpus} CPUs`}
                  />
                  <Chip
                    label={vm.state}
                    color={vm.state === 'running' ? 'success' : 'default'}
                    size="small"
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default TerminalPage; 